import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { API_BASE } from "../utils/apiBase";
import { RUNTIME_PUBLIC_BASE } from "../utils/runtimeBase";

const cardStyle = {
  background: "#fff",
  borderRadius: "22px",
  border: "1px solid rgba(148,163,184,0.16)",
  boxShadow: "0 18px 36px rgba(15,23,42,0.08)",
  padding: "20px",
};

const initialForm = {
  account_name: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  branch_name: "",
  is_active: true,
};
const initialQrForm = { qr_image: null, is_active: true };

function PaymentAccountSetting() {
  const [form, setForm] = useState(initialForm);
  const [qrForm, setQrForm] = useState(initialQrForm);
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadRows = async () => {
    const res = await axios.get(`${API_BASE}/company-account-setting`, { params: { _ts: Date.now() } });
    setRows(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    loadRows().catch(() => setMessage("Account setting load failed"));
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setQrForm(initialQrForm);
    setEditingId(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");
      const payload = new FormData();
      payload.append("account_name", form.account_name || "");
      payload.append("bank_name", form.bank_name || "");
      payload.append("account_number", form.account_number || "");
      payload.append("ifsc_code", form.ifsc_code || "");
      payload.append("branch_name", form.branch_name || "");
      payload.append("is_active", form.is_active ? "1" : "0");

      if (editingId) {
        await axios.post(`${API_BASE}/company-account-setting/${editingId}`, payload);
        setMessage("Account updated successfully");
      } else {
        await axios.post(`${API_BASE}/company-account-setting`, payload);
        setMessage("Account added successfully");
      }

      resetForm();
      await loadRows();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      account_name: row.account_name || "",
      bank_name: row.bank_name || "",
      account_number: row.account_number || "",
      ifsc_code: row.ifsc_code || "",
      branch_name: row.branch_name || "",
      is_active: !!row.is_active,
    });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/company-account-setting/${id}`);
      setMessage("Account deleted successfully");
      if (editingId === id) resetForm();
      await loadRows();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Delete failed");
    }
  };

  const handleToggleVisibility = async (row, checked) => {
    try {
      const payload = new FormData();
      payload.append("account_name", row.account_name || "");
      payload.append("bank_name", row.bank_name || "");
      payload.append("account_number", row.account_number || "");
      payload.append("ifsc_code", row.ifsc_code || "");
      payload.append("branch_name", row.branch_name || "");
      payload.append("is_active", checked ? "1" : "0");
      await axios.post(`${API_BASE}/company-account-setting/${row.id}`, payload);
      setMessage("Visibility updated");
      await loadRows();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Visibility update failed");
    }
  };

  const detailRows = useMemo(
    () => rows.filter((r) => (r.account_name || r.bank_name || r.account_number || r.ifsc_code || r.branch_name)),
    [rows]
  );
  const qrRows = useMemo(() => rows.filter((r) => r.qr_image), [rows]);

  const handleAddQrOnly = async () => {
    try {
      if (!qrForm.qr_image) {
        setMessage("QR image select korun");
        return;
      }
      setSaving(true);
      setMessage("");
      const payload = new FormData();
      payload.append("is_active", qrForm.is_active ? "1" : "0");
      payload.append("qr_image", qrForm.qr_image);
      await axios.post(`${API_BASE}/company-account-setting`, payload);
      setMessage("QR added successfully");
      setQrForm(initialQrForm);
      await loadRows();
    } catch (error) {
      setMessage(error?.response?.data?.message || "QR add failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ display: "grid", gap: "16px" }}>
        <div style={cardStyle}>
          <h4 style={{ marginTop: 0, marginBottom: "6px", color: "#0f172a" }}>Payment Account Setting</h4>
        </div>

        <div style={{ ...cardStyle, display: "grid", gap: "14px" }}>
          <h6 style={{ margin: 0 }}>Account Details</h6>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "14px" }}>
            <input className="form-control" placeholder="Account Holder Name" value={form.account_name} onChange={(e) => setForm((p) => ({ ...p, account_name: e.target.value }))} />
            <input className="form-control" placeholder="Bank Name" value={form.bank_name} onChange={(e) => setForm((p) => ({ ...p, bank_name: e.target.value }))} />
            <input className="form-control" placeholder="Account Number" value={form.account_number} onChange={(e) => setForm((p) => ({ ...p, account_number: e.target.value }))} />
            <input className="form-control" placeholder="IFSC Code" value={form.ifsc_code} onChange={(e) => setForm((p) => ({ ...p, ifsc_code: e.target.value }))} />
            <input className="form-control" placeholder="Branch Name" value={form.branch_name} onChange={(e) => setForm((p) => ({ ...p, branch_name: e.target.value }))} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={handleSave}>
              {saving ? "Saving..." : editingId ? "Update" : "Add Account"}
            </button>
            {editingId ? (
              <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancel Edit</button>
            ) : null}
            {message ? <span style={{ color: "#7c2d3a", fontWeight: 600 }}>{message}</span> : null}
          </div>
        </div>

        <div style={cardStyle}>
          <h5 style={{ marginTop: 0 }}>Account Details List</h5>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Bank</th>
                  <th>Number</th>
                  <th>IFSC</th>
                  <th>Branch</th>
                  <th>Show in Franchise</th>
                  <th style={{ width: 140 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.length ? detailRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.account_name || "-"}</td>
                    <td>{row.bank_name || "-"}</td>
                    <td>{row.account_number || "-"}</td>
                    <td>{row.ifsc_code || "-"}</td>
                    <td>{row.branch_name || "-"}</td>
                    <td>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!row.is_active}
                          onChange={(e) => handleToggleVisibility(row, e.target.checked)}
                        />
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(row)}>Edit</button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7}>No account found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ ...cardStyle, display: "grid", gap: "12px" }}>
          <h5 style={{ margin: 0 }}>QR Add (Separate)</h5>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", alignItems: "center" }}>
            <input type="file" className="form-control" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setQrForm((p) => ({ ...p, qr_image: e.target.files?.[0] || null }))} />
            <div className="form-check">
              <input id="qr_active" className="form-check-input" type="checkbox" checked={!!qrForm.is_active} onChange={(e) => setQrForm((p) => ({ ...p, is_active: e.target.checked }))} />
              <label htmlFor="qr_active" className="form-check-label">Active</label>
            </div>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={handleAddQrOnly}>Add QR</button>
          </div>
        </div>

        <div style={cardStyle}>
          <h5 style={{ marginTop: 0 }}>QR List</h5>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>QR</th>
                  <th>Show in Franchise</th>
                  <th style={{ width: 140 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {qrRows.length ? qrRows.map((row) => (
                  <tr key={`qr-${row.id}`}>
                    <td>{row.qr_image ? <img src={`${RUNTIME_PUBLIC_BASE}/uploads/company-account/${row.qr_image}`} alt="QR" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8 }} /> : "-"}</td>
                    <td>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!row.is_active}
                          onChange={(e) => handleToggleVisibility(row, e.target.checked)}
                        />
                      </div>
                    </td>
                    <td>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>Delete</button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={3}>No QR found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default PaymentAccountSetting;
